import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  Camera,
  AlertTriangle,
  Clock,
  DollarSign,
  TrendingUp,
  Activity,
  MapPin,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { MetricCard } from "@/components/common/metric-card";
import { GlobalFilterBar } from "@/components/common/global-filter-bar";
import { PerformanceTrendChart } from "@/components/common/performance-trend-chart";
import { ProblemPlateDetailsModal } from "@/components/common/problem-plate-details-modal";
import { InsightsCard } from "@/components/common/insights-card";
import { analyticsAPI } from "@/api";
import { useApi, useToastHelpers } from "@/hooks";
import { useDateRange } from "@/contexts/DateRangeContext";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatRelativeTime,
} from "@/lib/utils";
import {
  exportProblemPlatesToCSV,
  generateCSVFilename,
} from "@/lib/csv-export";
import { formatDateForAPI } from "@/lib/date-utils";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CHART_COLORS = ["#E86E30", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

export function Dashboard() {
  const navigate = useNavigate();
  const { dateRange, selectedLocations } = useDateRange();
  const { success: showToastSuccess, error: showToastError } =
    useToastHelpers();
  const locationParam = React.useMemo(
    () =>
      selectedLocations.length > 0 ? selectedLocations.join(",") : undefined,
    [selectedLocations],
  );

  // Fetch KPIs only when filters change (no background polling)
  const {
    data: dashboardResponse,
    isLoading: kpisLoading,
    execute: fetchKPIs,
  } = useApi(analyticsAPI.getDashboardKPIs, { returnFullResponse: true });

  const startDateStr = formatDateForAPI(dateRange.from);
  const endDateStr = formatDateForAPI(dateRange.to);

  // Debug logging
  console.log("Dashboard date range:", {
    label: dateRange.label,
    from: dateRange.from,
    to: dateRange.to,
    startDateStr,
    endDateStr,
  });

  const filtersKey = React.useMemo(
    () => `${startDateStr}:${endDateStr}:${locationParam ?? "all"}`,
    [startDateStr, endDateStr, locationParam],
  );
  const lastKpiKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const key = `kpi:${filtersKey}`;
    if (lastKpiKeyRef.current === key) {
      return;
    }
    lastKpiKeyRef.current = key;

    fetchKPIs({
      start_date: startDateStr,
      end_date: endDateStr,
      ...(locationParam ? { location: locationParam } : {}),
    }).catch((error) => {
      console.error("Failed to fetch KPIs:", error);
      lastKpiKeyRef.current = null;
    });
  }, [fetchKPIs, filtersKey, startDateStr, endDateStr, locationParam]);

  // Extract KPI values from the response structure
  const getKPIValue = (label: string): number => {
    const kpis = dashboardResponse?.data?.kpis || dashboardResponse?.kpis;
    if (!kpis) {
      return 0;
    }
    const kpi = kpis.find((k) => k.label === label);
    return kpi?.value || 0;
  };

  // Fetch time series data for charts
  const {
    data: timeSeriesResponse,
    isLoading: chartLoading,
    execute: fetchTimeSeries,
  } = useApi(analyticsAPI.getTimeSeriesData);
  const lastTimeSeriesKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const key = `series:${filtersKey}`;
    if (lastTimeSeriesKeyRef.current === key) {
      return;
    }
    lastTimeSeriesKeyRef.current = key;

    fetchTimeSeries({
      start_date: startDateStr,
      end_date: endDateStr,
      metric: "sessions_count",
      granularity: "hour",
      ...(locationParam ? { location: locationParam } : {}),
    }).catch((error) => {
      console.error("Failed to fetch time series:", error);
      lastTimeSeriesKeyRef.current = null;
    });
  }, [fetchTimeSeries, filtersKey, startDateStr, endDateStr, locationParam]);

  // Fetch problem plates (top 20 for display)
  const [problemPlatesLimit, setProblemPlatesLimit] = React.useState(20);
  const {
    data: problemPlatesResponse,
    isLoading: problemPlatesLoading,
    execute: fetchProblemPlates,
  } = useApi(analyticsAPI.getProblemPlates);
  const lastProblemKeyRef = React.useRef<string | null>(null);
  const problemPlateParams = React.useMemo(
    () => ({
      start_date: startDateStr,
      end_date: endDateStr,
      min_occurrences: 2,
      limit: problemPlatesLimit,
      ...(locationParam ? { location: locationParam } : {}),
    }),
    [startDateStr, endDateStr, problemPlatesLimit, locationParam],
  );

  React.useEffect(() => {
    const key = `problems:${filtersKey}:${problemPlatesLimit}`;
    if (lastProblemKeyRef.current === key) {
      return;
    }
    lastProblemKeyRef.current = key;

    fetchProblemPlates(problemPlateParams).catch((error) => {
      console.error("Failed to fetch problem plates:", error);
      lastProblemKeyRef.current = null;
    });
  }, [fetchProblemPlates, filtersKey, problemPlatesLimit, problemPlateParams]);

  const {
    data: problemPlateExclusions,
    isLoading: exclusionsLoading,
    execute: fetchProblemPlateExclusions,
  } = useApi(analyticsAPI.getProblemPlateExclusions);
  const [newExclusionPlate, setNewExclusionPlate] = React.useState("");
  const [newExclusionState, setNewExclusionState] = React.useState("");
  const [isUpdatingExclusions, setIsUpdatingExclusions] = React.useState(false);

  React.useEffect(() => {
    fetchProblemPlateExclusions().catch((error) => {
      console.error("Failed to load exclusion list:", error);
    });
  }, [fetchProblemPlateExclusions]);

  const refreshProblemPlates = React.useCallback(async () => {
    lastProblemKeyRef.current = null;
    await fetchProblemPlates(problemPlateParams);
  }, [fetchProblemPlates, problemPlateParams]);

  const handleAddExclusion = async () => {
    const plateValue = newExclusionPlate.trim();
    if (!plateValue) {
      showToastError("Plate is required", "Enter a plate value to exclude.");
      return;
    }

    setIsUpdatingExclusions(true);
    try {
      await analyticsAPI.addProblemPlateExclusion({
        plate: plateValue,
        ...(newExclusionState.trim()
          ? { state: newExclusionState.trim() }
          : {}),
      });
      setNewExclusionPlate("");
      setNewExclusionState("");
      await fetchProblemPlateExclusions();
      await refreshProblemPlates();
      showToastSuccess(
        "Plate excluded",
        `${plateValue.toUpperCase()} hidden from problem plates.`,
      );
    } catch (error: any) {
      console.error("Failed to add exclusion:", error);
      const message =
        error?.response?.data?.detail ||
        "Unable to add plate to exclusion list.";
      showToastError("Failed to exclude plate", message);
    } finally {
      setIsUpdatingExclusions(false);
    }
  };

  const handleRemoveExclusion = async (plate: string) => {
    setIsUpdatingExclusions(true);
    try {
      await analyticsAPI.removeProblemPlateExclusion(plate);
      await fetchProblemPlateExclusions();
      await refreshProblemPlates();
      showToastSuccess(
        "Plate removed",
        `${plate} will appear in problem plates again.`,
      );
    } catch (error: any) {
      console.error("Failed to remove exclusion:", error);
      const message =
        error?.response?.data?.detail ||
        "Unable to remove plate from exclusion list.";
      showToastError("Failed to remove plate", message);
    } finally {
      setIsUpdatingExclusions(false);
    }
  };

  // Problem plate drill-down state
  const [selectedPlate, setSelectedPlate] = React.useState<string | null>(null);
  const [plateDetails, setPlateDetails] = React.useState<any>(null);
  const [loadingPlateDetails, setLoadingPlateDetails] = React.useState(false);

  // Function to fetch and show plate details
  const handlePlateClick = async (plate: string) => {
    setSelectedPlate(plate);
    setLoadingPlateDetails(true);
    try {
      const response = await analyticsAPI.getProblemPlateDetails(plate, {
        start_date: formatDateForAPI(dateRange.from),
        end_date: formatDateForAPI(dateRange.to),
        ...(selectedLocations.length > 0 && {
          location: selectedLocations.join(","),
        }),
      });
      setPlateDetails(response?.data || response);
    } catch (error) {
      console.error("Failed to fetch plate details:", error);
    } finally {
      setLoadingPlateDetails(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedPlate(null);
    setPlateDetails(null);
  };

  // Function to export ALL problem plates
  const handleExportProblemPlates = async () => {
    try {
      const response = await analyticsAPI.getProblemPlates({
        ...problemPlateParams,
        limit: 1000,
      });

      const allPlates = response?.data || [];
      if (allPlates.length === 0) {
        showToastError(
          "No data to export",
          "There are no problem plates in the current view.",
        );
        return;
      }

      const filename = generateCSVFilename("problem-plates");
      exportProblemPlatesToCSV(allPlates, filename);
    } catch (error) {
      console.error("Export failed:", error);
      showToastError(
        "Failed to export",
        "Unable to export the current problem plates list.",
      );
    }
  };

  // Fetch performance trends for advanced chart
  const {
    data: trendsResponse,
    isLoading: trendsLoading,
    execute: fetchTrends,
  } = useApi(analyticsAPI.getPerformanceTrends);
  const lastTrendsKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const key = `trends:${filtersKey}`;
    if (lastTrendsKeyRef.current === key) {
      return;
    }
    lastTrendsKeyRef.current = key;

    fetchTrends({
      start_date: startDateStr,
      end_date: endDateStr,
      ...(locationParam ? { location: locationParam } : {}),
    }).catch((error) => {
      console.error("Failed to fetch trends:", error);
      lastTrendsKeyRef.current = null;
    });
  }, [fetchTrends, filtersKey, startDateStr, endDateStr, locationParam]);

  // Mock data for demonstration
  const recentSessions = [];

  const cameraStatuses = [];

  const handleMetricClick = (filter?: { key: string; value: any }) => {
    if (filter) {
      // Navigate to sessions with filter applied
      navigate("/sessions", { state: { filter } });
    } else {
      // Navigate to all sessions
      navigate("/sessions");
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log("Exporting dashboard data...");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time overview of your parking management system
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Global Filters */}
      <GlobalFilterBar />

      {/* Primary KPI Cards - Large and Prominent */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          title="Total Reads"
          value={getKPIValue("Total Reads")}
          description="Entry + Exit records"
          icon={Activity}
          loading={kpisLoading}
          className="lg:col-span-1"
        />

        <MetricCard
          title="Total Sessions"
          value={getKPIValue("Total Sessions")}
          description="Matched entry-exit pairs"
          icon={Car}
          onClick={() => handleMetricClick()}
          loading={kpisLoading}
          className="lg:col-span-1"
        />

        <MetricCard
          title="Match Rate"
          value={`${getKPIValue("Match Rate")}%`}
          description="Sessions / Total Reads"
          icon={TrendingUp}
          onClick={() => handleMetricClick()}
          loading={kpisLoading}
          className="lg:col-span-1"
        />
      </div>

      {/* Session Type Breakdown */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          Session Duration Breakdown
        </h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Same-Day"
            value={getKPIValue("Same-Day Sessions")}
            description="≤ 24 hours"
            icon={Clock}
            onClick={() =>
              handleMetricClick({ key: "duration_type", value: "same_day" })
            }
            loading={kpisLoading}
          />

          <MetricCard
            title="Overnight"
            value={getKPIValue("Overnight Sessions")}
            description="24-48 hours"
            icon={Clock}
            onClick={() =>
              handleMetricClick({ key: "duration_type", value: "overnight" })
            }
            loading={kpisLoading}
          />

          <MetricCard
            title="Multi-Day"
            value={getKPIValue("Multi-Day Sessions")}
            description="> 48 hours"
            icon={Calendar}
            onClick={() =>
              handleMetricClick({ key: "duration_type", value: "multiday" })
            }
            loading={kpisLoading}
          />
        </div>
      </div>

      {/* Match Quality & Issues */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Match Quality & Issues</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Exact Matches"
            value={getKPIValue("Exact Matches")}
            description="Perfect plate match"
            icon={Car}
            onClick={() =>
              handleMetricClick({ key: "is_fuzzy", value: "false" })
            }
            loading={kpisLoading}
          />

          <MetricCard
            title="Fuzzy Matches"
            value={getKPIValue("Fuzzy Matches")}
            description="Character confusion"
            icon={AlertTriangle}
            onClick={() =>
              handleMetricClick({ key: "is_fuzzy", value: "true" })
            }
            loading={kpisLoading}
            badge={{ text: "Confusables", variant: "warning" }}
          />

          <MetricCard
            title="State Mismatches"
            value={getKPIValue("State Mismatches")}
            description="Entry ≠ Exit state"
            icon={MapPin}
            onClick={() =>
              handleMetricClick({ key: "state_mismatch", value: "true" })
            }
            loading={kpisLoading}
            badge={{ text: "Review", variant: "warning" }}
          />

          <MetricCard
            title="Orphaned Records"
            value={getKPIValue("Orphaned Records")}
            description="No match found"
            icon={AlertTriangle}
            onClick={() => navigate("/orphans")}
            loading={kpisLoading}
            badge={{ text: "Action needed", variant: "destructive" }}
          />
        </div>
      </div>

      {/* Performance Trends Chart - Full Width */}
      <PerformanceTrendChart
        data={trendsResponse?.all_locations || []}
        byLocationData={trendsResponse?.by_location || {}}
        loading={trendsLoading}
        targetMatchRate={85}
      />

      {/* AI Insights */}
      <InsightsCard locationId={locationParam} maxItems={5} />

      {/* Problem Plates - Actionable Issues */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <span>Problem Plates</span>
              </CardTitle>
              <CardDescription>
                Plates appearing in orphaned records multiple times - indicates
                recurring LPR issues
              </CardDescription>
            </div>
            {problemPlatesResponse && problemPlatesResponse.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportProblemPlates}
              >
                <Download className="h-4 w-4 mr-2" />
                Export All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-dashed border-border/60 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
              <div className="max-w-2xl">
                <h4 className="font-semibold">Exclude known plates</h4>
                <p className="text-sm text-muted-foreground">
                  Hidden plates are removed from this list and CSV exports.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <Input
                  placeholder="Plate"
                  value={newExclusionPlate}
                  onChange={(event) => setNewExclusionPlate(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleAddExclusion();
                    }
                  }}
                  containerClassName="w-full sm:w-48 lg:w-56"
                  className="w-full"
                  disabled={isUpdatingExclusions}
                />
                <Input
                  placeholder="State (optional)"
                  value={newExclusionState}
                  onChange={(event) => setNewExclusionState(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleAddExclusion();
                    }
                  }}
                  containerClassName="w-full sm:w-40 lg:w-48"
                  className="w-full"
                  disabled={isUpdatingExclusions}
                />
                <Button
                  size="sm"
                  onClick={handleAddExclusion}
                  disabled={isUpdatingExclusions}
                  className="w-full sm:w-auto"
                >
                  Add
                </Button>
              </div>
            </div>
            <div className="mt-4">
              {exclusionsLoading ? (
                <div className="text-sm text-muted-foreground">
                  Loading exclusions...
                </div>
              ) : problemPlateExclusions &&
                problemPlateExclusions.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {problemPlateExclusions.map((item) => (
                    <Badge
                      key={item.plate}
                      variant="secondary"
                      className="flex items-center gap-2 px-3 py-1"
                    >
                      <span className="font-mono font-semibold">
                        {item.plate}
                      </span>
                      {item.state && (
                        <span className="text-xs text-muted-foreground">
                          {item.state}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveExclusion(item.plate)}
                        className="ml-1 text-muted-foreground hover:text-foreground"
                        disabled={isUpdatingExclusions}
                        aria-label={`Remove ${item.plate}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No plates currently excluded.
                </div>
              )}
            </div>
          </div>

          {problemPlatesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-muted-foreground">
                Loading problem plates...
              </div>
            </div>
          ) : !problemPlatesResponse || problemPlatesResponse.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 text-green-600 mx-auto mb-2" />
                <div className="text-lg font-semibold text-green-600">
                  No problem plates found!
                </div>
                <div className="text-sm text-muted-foreground">
                  All plates are matching cleanly
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Plate</th>
                    <th className="text-left p-3 font-semibold">State</th>
                    <th className="text-left p-3 font-semibold">Locations</th>
                    <th className="text-right p-3 font-semibold">
                      Total Orphans
                    </th>
                    <th className="text-left p-3 font-semibold">
                      Record Types
                    </th>
                    <th className="text-left p-3 font-semibold">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {problemPlatesResponse.map((plate, idx) => (
                    <tr key={idx} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <button
                          onClick={() => handlePlateClick(plate.plate)}
                          className="font-mono font-bold text-lg text-primary hover:underline cursor-pointer"
                        >
                          {plate.plate}
                        </button>
                      </td>
                      <td className="p-3">{plate.state}</td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {plate.location_ids}
                      </td>
                      <td className="p-3 text-right">
                        <Badge variant="destructive" className="font-bold">
                          {plate.orphan_count}x
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{plate.record_types}</Badge>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {new Date(plate.last_seen).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Show More / Show Less controls */}
              {problemPlatesResponse &&
                problemPlatesResponse.length >= problemPlatesLimit && (
                  <div className="flex justify-center mt-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setProblemPlatesLimit((prev) => prev + 20)}
                    >
                      Show More (20+)
                    </Button>
                  </div>
                )}

              {problemPlatesLimit > 20 && (
                <div className="flex justify-center mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setProblemPlatesLimit(20)}
                  >
                    Show Less
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Problem Plate Details Modal */}
      <ProblemPlateDetailsModal
        plate={selectedPlate}
        isOpen={!!selectedPlate && !loadingPlateDetails}
        onClose={handleCloseModal}
        entries={plateDetails?.entries || []}
        exits={plateDetails?.exits || []}
        totalOrphans={plateDetails?.total_orphans || 0}
      />
    </div>
  );
}
